/**
 * IMPORTANT:
 * useCheckbox hook is used in combination with Zerpatable.
 * This hook must be used with zerpatable when ZerpaTable's
 * checkbox feature is enabled.
 *
 * CheckboxSelection has to be passed to ZerpaTable to
 * enable checkbox selection mode.
 *
 * This hook provides local state management as well as with Redux
 */

import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  setZerpaSelectedCheckbox,
} from '../../../redux/actions';
import {
  RootState,
  PageIdentifier,
} from '../../../redux/types';

export type SetZerpaSelectedIdsActions = (ids: unknown[]) => void;

export class CheckboxSelection {
  public selectedIds: unknown[];

  public setSelectedIds: React.Dispatch<React.SetStateAction<unknown[]>>
  | SetZerpaSelectedIdsActions;

  constructor(
    selectedIds: unknown[],
    setSelectedIds: React.Dispatch<React.SetStateAction<unknown[]>> | SetZerpaSelectedIdsActions,
  ) {
    this.selectedIds = selectedIds;
    this.setSelectedIds = setSelectedIds;
  }

  public clearSelectedIds = (): void => {
    this.setSelectedIds([]);
  };
}

/**
 * Use this hook for the react state management in the component
 * where this hook is called.
 */
export default function useCheckbox(): CheckboxSelection {
  const [selectedIds, setSelectedIds] = useState<unknown[]>([]);
  return new CheckboxSelection(selectedIds, setSelectedIds);
}

/**
 * This hook saves the zerpa table selected checkbox state
 * in redux store. Page identifier has to be provided so that
 * each page (or view) will have its states maintained separately
 *
 * @param pageIdentifier page identifier under which this
 * checkbox selection state will be saved in redux store
 */
export const useCheckboxRedux = (pageIdentifier: PageIdentifier): CheckboxSelection => {
  const dispatch = useDispatch();
  const selectedIds = useSelector((state: RootState) => (
    state.pageState[pageIdentifier].selectedCheckboxIds
  ));
  const setSelectedIds = (selectedCheckboxIds: unknown[]) => {
    dispatch(setZerpaSelectedCheckbox(pageIdentifier, selectedCheckboxIds as string[] | number[]));
  };
  return new CheckboxSelection(selectedIds as unknown[], setSelectedIds);
};
